import { SfChartWithoutInterface } from "@/components/core/charts/sfChartWithoutInterface.tsx";
import { SfTable } from "@/components/core/sfTable.tsx";
import { usePaneContext } from "@/components/pane/paneContext.ts";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable.tsx";
import { ESFTableType, EWidgetType } from "@/domain/enums/dashboard.enum.ts";
import {
  IColumn,
  IRow,
  ISfTable,
} from "@/domain/interfaces/sfTable.interface.ts";
import { useGrid } from "@/hooks/useGrid.ts";
import { useDashboard } from "@/lib/store.ts";
import { SortingState } from "@tanstack/react-table";
import { debounce } from "lodash";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useUserStore } from "@/lib/stores/userStore";
import { hasAccessToWidget } from "@/domain/constants";
import { useMarketplaceStore } from "@/lib/stores/useMarketplaceStore";
import { logAnalyticsEvent } from "@/lib/firebase";

interface IPaneBody {
  data: IRow[];
  tableColumns: IColumn[];
  rowClassCb?: ISfTable<IRow<any>>["rowClassCb"];
  className?: string;
  rowClickable?: boolean;
}

function sortData(data: IRow[], sorting: SortingState) {
  return [...data].sort((a, b) => {
    for (let i = 0; i < sorting.length; i++) {
      const { id, desc } = sorting[i];
      if (a[id] < b[id]) {
        return desc ? 1 : -1;
      }
      if (a[id] > b[id]) {
        return desc ? -1 : 1;
      }
    }
    return 0;
  });
}

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text).catch((err) => {
    console.error("Failed to copy text: ", err);
  });
};

export function PaneBody({
  data: __data,
  tableColumns,
  rowClassCb,
  className,
  rowClickable = true,
}: IPaneBody) {
  const nodeId = usePaneContext();
  const { node, getNodeConfig, updateTabConfig } = useGrid(nodeId);
  const widgetType = node?.getComponent() as EWidgetType;
  const [data, setData] = useState(__data);
  const isPaused = getNodeConfig().paused;
  const config = getNodeConfig();
  const component = node?.getComponent();
  const { user } = useUserStore();
  const { purchasedProductIds } = useMarketplaceStore();

  // sent event to ga
  useEffect(() => {
    // Log event when component mounts
    logAnalyticsEvent("pane_view", {
      widget_type: widgetType,
      user_role: user?.role,
      has_purchased: purchasedProductIds.length > 0,
    });
  }, []); // Empty dependency array means this runs once when component mounts

  useEffect(() => {
    if (!isPaused) {
      if (JSON.stringify(data) !== JSON.stringify(__data)) {
        setData(__data);
      }
    }
  }, [__data, isPaused, data]);

  const filterByFavorites = getNodeConfig()?.filterByFavorites;
  const columnSizes = useMemo(() => {
    return getNodeConfig()?.columnSizes ?? {};
  }, [getNodeConfig]);

  const { pinned: pinState, setSymbolForColor } = useDashboard((state) => ({
    setSymbolForColor: state.setSymbolForColor,
    pinned: state.pinned,
  }));

  const sortingState = useMemo(() => {
    return (getNodeConfig()?.sortingState ?? []) as SortingState;
  }, [getNodeConfig]);

  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (!filterByFavorites || !filterByFavorites.length) {
        return true;
      }

      // Split complex symbol into parts
      const [firstSymbol, secondSymbol] = row.symbol.split("/");

      // Get both full symbols and base symbols (without USDT)
      const symbols = [firstSymbol, secondSymbol].filter(Boolean);

      return symbols.some((symbol) => filterByFavorites.includes(symbol));
    });
  }, [data, filterByFavorites]);

  const updatedData = useMemo(() => {
    const pinned: IRow[] = [];
    const unpinned: IRow[] = [];
    const pinnedSymbolsSet = new Set(pinState);

    filteredData.forEach((row) => {
      if (pinnedSymbolsSet.has(row.symbol as string)) {
        pinned.push(row);
        pinnedSymbolsSet.delete(row.symbol as string);
      } else {
        unpinned.push(row);
      }
    });

    if (pinnedSymbolsSet.size > 0) {
      pinnedSymbolsSet.forEach((symbol) => {
        pinned.push({ symbol, value: 0 });
      });
    }

    const sortedPinned = sortData(pinned, sortingState);
    const sortedUnpinned = sortData(unpinned, sortingState);
    const numberOfCharts =
      getNodeConfig()?.numberOfCharts ??
      sortedPinned.length + sortedUnpinned.length;

    return {
      pinned: sortedPinned,
      unpinned: sortedUnpinned,
      combinedData: [...sortedPinned, ...sortedUnpinned].slice(
        0,
        numberOfCharts,
      ),
    };
  }, [filteredData, pinState, sortingState, getNodeConfig]);

  const updatedTableColumns = useMemo(() => {
    return tableColumns.map((column) => {
      if (columnSizes) {
        const size = columnSizes[column?.accessorKey];
        if (size) {
          return { ...column, size };
        }
      }
      return column;
    });
  }, [columnSizes, tableColumns]);

  const style = useMemo(
    () => ({
      height: `${100 / (updatedData.combinedData.length || 1)}%`,
    }),
    [updatedData.combinedData.length],
  );

  const onLayout = (sizes: number[]) => {
    updateTabConfig({ resizeArray: sizes });
  };

  const debouncedOnLayout = debounce(onLayout, 300);

  const MIN_SIZE_IN_PIXELS = 22;
  const [minSize, setMinSize] = useState(1);

  useLayoutEffect(() => {
    if (!node) {
      return;
    }

    let observer: ResizeObserver | null = null;
    const panelGroup = document.querySelector(
      `[data-panel-group-id="${node?.getId()}"]`,
    );

    if (panelGroup) {
      observer = new ResizeObserver(() => {
        const totalHeight = (panelGroup as HTMLElement).offsetHeight;
        if (totalHeight > 0) {
          const minSizePercentage = (MIN_SIZE_IN_PIXELS / totalHeight) * 100;
          setMinSize(Math.max(minSizePercentage, 1));
        }
      });

      observer.observe(panelGroup);
    }

    return () => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    };
  }, [node]);

  const renderChart = useCallback(
    (item: IRow, idx: number, isPinned: boolean) => {
      const symbol = item.symbol as string;
      const symbolToCopy = symbol.includes("/")
        ? `Binance:${symbol.split("/")[0]}/Binance:${symbol.split("/")[1]}`
        : symbol;
      return (
        <div
          key={idx}
          style={{
            ...(config?.isChartGrid
              ? {
                  flex: "1 1 300px",
                  minWidth: "300px",
                  minHeight: "300px",
                  height: "calc((100% - 16px) / 3)",
                  maxHeight: "calc((100% - 16px) / 3)",
                }
              : {
                  width: "100%",
                  height: `${100 / (updatedData.combinedData.length || 1)}%`,
                }),
          }}
          className="relative"
        >
          <div className="text-xs w-full">
            <SfTable
              data={{
                unpinned: isPinned ? [] : [item],
                pinned: isPinned ? [item] : [],
              }}
              withHeader={false}
              tableType={[ESFTableType.chart]}
              columns={updatedTableColumns}
              className="pr-2 w-[calc(100%-4px)]"
              onRowClick={(row) => {
                if (rowClickable) {
                  const symbol = row.symbol as string;
                  copyToClipboard(row.symbol as string);
                  setSymbolForColor(getNodeConfig().color, symbolToCopy);
                }
              }}
            />
          </div>
          <div className="h-[calc(100%-16px)] relative">
            <SfChartWithoutInterface
              symbol={symbolToCopy}
              interval={config?.interval}
            />
          </div>
        </div>
      );
    },
    [
      updatedTableColumns,
      config?.interval,
      getNodeConfig,
      setSymbolForColor,
      updatedData.combinedData.length,
      config?.isChartGrid,
      rowClickable,
    ],
  );

  if (!node || !config || !component) {
    return null;
  }

  if (
    !hasAccessToWidget(user?.role || "GUEST", widgetType, purchasedProductIds)
  ) {
    return (
      <div className="flex flex-col h-[calc(100%-48px)] items-center justify-center space-y-2">
        <p>No access</p>
        <div className="text-sm">Upgrade your plan to {config?.plan}</div>

        <i
          onClick={() => {
            window.open("https://marketplace.spreadfighter.com", "_blank");
          }}
          className="cursor-pointer text-7xl fa-brands fa-shopify text-green-500"
        ></i>
      </div>
    );
  }

  return (
    <ResizablePanelGroup
      direction="vertical"
      onLayout={debouncedOnLayout}
      id={node.getId()}
      className={className}
    >
      {/*table*/}
      <ResizablePanel defaultSize={config.resizeArray?.[0] ?? 50}>
        <div className="h-full overflow-auto">
          <SfTable
            data={{
              pinned: updatedData.pinned,
              unpinned: updatedData.unpinned,
            }}
            columns={updatedTableColumns}
            tableType={[]}
            rowClassCb={rowClassCb}
            className="h-full overflow-auto"
            onRowClick={(row) => {
              if (rowClickable) {
                const symbol = row.symbol as string;
                copyToClipboard(row.symbol as string);
                setSymbolForColor(
                  getNodeConfig().color,
                  symbol.includes("/")
                    ? `Binance:${symbol.split("/")[0]}/Binance:${symbol.split("/")[1]}`
                    : symbol,
                );
              }
            }}
          />
        </div>
      </ResizablePanel>

      {/*charts*/}
      {config?.numberOfCharts > 0 && (
        <>
          <ResizableHandle
            className="min-h-4 bg-stone-950 hover:bg-stone-600/40 transition duration-250"
            withHandle={true}
          />

          <ResizablePanel defaultSize={config.resizeArray?.[1] ?? 50}>
            <div
              className={`h-full overflow-auto p-2 ${
                config?.isChartGrid
                  ? "flex flex-wrap content-start gap-2"
                  : "flex flex-col gap-2"
              }`}
            >
              {updatedData.combinedData.map((item, i) =>
                renderChart(item, i, i < updatedData.pinned.length),
              )}
            </div>
          </ResizablePanel>
        </>
      )}
    </ResizablePanelGroup>
  );
}
